import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';
import type { MediaItem } from './mediaSlice';

/** Search retrieval mode. Keyword is the default for backward compatibility. */
export type SearchMode = 'keyword' | 'semantic' | 'hybrid';

export interface SearchFilters {
  query: string;
  tags: string;
  /** Folder path prefix filter (replaces event/department) */
  folder: string;
  author: string;
  /** Retrieval mode: keyword (default), semantic, or hybrid */
  mode: SearchMode;
  /** @deprecated kept for backward compat during migration */
  event?: string;
  /** @deprecated kept for backward compat during migration */
  department?: string;
}

export interface SearchState {
  filters: SearchFilters;
  results: MediaItem[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  /** Whether a search has been submitted at least once */
  hasSearched: boolean;
  /** Set when a semantic/hybrid request fell back to keyword search */
  degradedToKeyword?: boolean;
  /** Set when a whole-library (no folder) search was bounded to recent media */
  scopeLimited?: boolean;
}

const initialState: SearchState = {
  filters: {
    query: '',
    tags: '',
    folder: '',
    author: '',
    mode: 'keyword',
  },
  results: [],
  total: 0,
  page: 1,
  pageSize: 20,
  loading: false,
  error: null,
  hasSearched: false,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

interface FetchSearchResultsParams {
  query?: string;
  tags?: string;
  folder?: string;
  author?: string;
  mode?: SearchMode;
  page?: number;
  pageSize?: number;
}

interface SearchApiResponse {
  items: MediaItem[];
  total: number;
  page: number;
  pageSize: number;
  mode?: SearchMode;
  degradedToKeyword?: boolean;
  scopeLimited?: boolean;
}

/**
 * Fetch search results from GET /media/search.
 * Requirements: 8.1, 8.2, 8.8, 8.9, 8.10, 8.11
 */
export const fetchSearchResults = createAsyncThunk(
  'search/fetchSearchResults',
  async (params: FetchSearchResultsParams) => {
    const queryParams = new URLSearchParams();

    if (params.query) queryParams.append('query', params.query);
    if (params.tags) queryParams.append('tags', params.tags);
    if (params.folder) queryParams.append('folder', params.folder);
    if (params.author) queryParams.append('author', params.author);
    if (params.mode) queryParams.append('mode', params.mode);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());

    const response = await apiClient.get<SearchApiResponse>(
      `/media/search?${queryParams.toString()}`,
    );

    return response.data;
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<Partial<SearchFilters>>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = initialState.filters;
      state.results = [];
      state.total = 0;
      state.page = 1;
      state.hasSearched = false;
    },
    setSearchResults(
      state,
      action: PayloadAction<{
        items: MediaItem[];
        total: number;
        page: number;
        pageSize: number;
        degradedToKeyword?: boolean;
      }>,
    ) {
      state.results = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
      state.degradedToKeyword = action.payload.degradedToKeyword;
      state.loading = false;
      state.error = null;
      state.hasSearched = true;
    },
    setSearchLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSearchError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
    setSearchPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSearchResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        state.results = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.degradedToKeyword = action.payload.degradedToKeyword;
        state.scopeLimited = action.payload.scopeLimited;
        state.loading = false;
        state.error = null;
        state.hasSearched = true;
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch search results';
        state.hasSearched = true;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setSearchResults,
  setSearchLoading,
  setSearchError,
  setSearchPage,
} = searchSlice.actions;

// Selectors
export const selectSearchFilters = (state: { search: SearchState }) => state.search.filters;
export const selectSearchResults = (state: { search: SearchState }) => state.search.results;
export const selectSearchTotal = (state: { search: SearchState }) => state.search.total;
export const selectSearchPage = (state: { search: SearchState }) => state.search.page;
export const selectSearchLoading = (state: { search: SearchState }) => state.search.loading;
export const selectSearchError = (state: { search: SearchState }) => state.search.error;
export const selectHasSearched = (state: { search: SearchState }) => state.search.hasSearched;
export const selectSearchDegradedToKeyword = (state: { search: SearchState }) =>
  state.search.degradedToKeyword;
export const selectSearchScopeLimited = (state: { search: SearchState }) =>
  state.search.scopeLimited;

export default searchSlice.reducer;
